import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import { COVERAGE } from '@/constants/testIds';

// Stylized 50-state dot-matrix map. Each entry is a state placed on a
// tight abstract grid (col, row) that roughly matches the geography of
// the continental US, with Alaska and Hawaii offset in the lower-left.
const STATES = [
	// row 1 — top
	{ code: 'WA', c: 2, r: 1 },
	{ code: 'ID', c: 3, r: 1 },
	{ code: 'MT', c: 4, r: 1 },
	{ code: 'ND', c: 5, r: 1 },
	{ code: 'MN', c: 6, r: 1 },
	{ code: 'WI', c: 7, r: 1 },
	{ code: 'MI', c: 8, r: 1 },
	{ code: 'NY', c: 10, r: 1 },
	{ code: 'VT', c: 11, r: 1 },
	{ code: 'NH', c: 12, r: 1 },
	{ code: 'ME', c: 13, r: 1 },
	// row 2
	{ code: 'OR', c: 2, r: 2 },
	{ code: 'NV', c: 3, r: 2 },
	{ code: 'WY', c: 4, r: 2 },
	{ code: 'SD', c: 5, r: 2 },
	{ code: 'IA', c: 6, r: 2 },
	{ code: 'IL', c: 7, r: 2 },
	{ code: 'IN', c: 8, r: 2 },
	{ code: 'OH', c: 9, r: 2 },
	{ code: 'PA', c: 10, r: 2 },
	{ code: 'NJ', c: 11, r: 2 },
	{ code: 'CT', c: 12, r: 2 },
	{ code: 'RI', c: 13, r: 2 },
	{ code: 'MA', c: 12, r: 1 }, // second slot
	// row 3
	{ code: 'CA', c: 2, r: 3 },
	{ code: 'UT', c: 3, r: 3 },
	{ code: 'CO', c: 4, r: 3 },
	{ code: 'NE', c: 5, r: 3 },
	{ code: 'MO', c: 6, r: 3 },
	{ code: 'KY', c: 7, r: 3 },
	{ code: 'WV', c: 9, r: 3 },
	{ code: 'VA', c: 10, r: 3 },
	{ code: 'MD', c: 11, r: 3 },
	{ code: 'DE', c: 12, r: 3 },
	{ code: 'TN', c: 8, r: 3 },
	// row 4
	{ code: 'AZ', c: 3, r: 4 },
	{ code: 'NM', c: 4, r: 4 },
	{ code: 'KS', c: 5, r: 4 },
	{ code: 'AR', c: 6, r: 4 },
	{ code: 'MS', c: 7, r: 4 },
	{ code: 'AL', c: 8, r: 4 },
	{ code: 'GA', c: 9, r: 4 },
	{ code: 'SC', c: 10, r: 4 },
	{ code: 'NC', c: 11, r: 4 },
	// row 5
	{ code: 'TX', c: 4, r: 5 },
	{ code: 'OK', c: 5, r: 5 },
	{ code: 'LA', c: 6, r: 5 },
	{ code: 'FL', c: 10, r: 5 },
	// AK & HI
	{ code: 'AK', c: 1, r: 5 },
	{ code: 'HI', c: 2, r: 5 },
];

const COLS = 14;
const ROWS = 5;

export const Coverage = () => {
	return (
		<section
			id="coverage"
			data-testid={COVERAGE.root}
			className="bg-brand-surface"
		>
			<div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 sm:py-32">
				<div className="grid md:grid-cols-12 gap-8 items-end mb-14 sm:mb-20">
					<div className="md:col-span-4">
						<div className="font-chivo text-xs uppercase tracking-[0.3em] text-brand-red font-bold mb-4">
							/ Coverage
						</div>
						<motion.p
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}
							className="font-plex text-brand-navy/75 text-[15px] leading-relaxed"
						>
							Dispatched from Sheridan, WY — connected to a
							vetted network of licensed operators in every
							corner of the country. Interstates. Rural
							highways. Suburban cul-de-sacs. If it has a road,
							we cover it.
						</motion.p>
					</div>
					<div className="md:col-span-8">
						<motion.h2
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.7 }}
							className="font-anton text-[40px] sm:text-[72px] md:text-[96px] leading-[0.92] text-brand-navy"
						>
							Now serving{' '}
							<span className="text-brand-red">
								all 50 states.
							</span>
						</motion.h2>
					</div>
				</div>

				<motion.div
					data-testid={COVERAGE.map}
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true, margin: '-100px' }}
					transition={{ duration: 0.9 }}
					className="relative border border-brand-navy bg-white p-4 sm:p-10"
				>
					<div className="absolute top-3 left-4 sm:top-5 sm:left-6 flex items-center gap-2 font-chivo text-[10px] uppercase tracking-[0.3em] font-bold text-brand-navy/60">
						<span className="w-2 h-2 bg-brand-red pulse-dot" />
						Live Dispatch Grid
					</div>
					<div className="absolute top-3 right-4 sm:top-5 sm:right-6 font-chivo text-[10px] uppercase tracking-[0.3em] font-bold text-brand-navy/60">
						USA · 50 / 50
					</div>

					<div
						className="mt-10 mb-6 grid gap-x-1.5 sm:gap-x-3 gap-y-2 sm:gap-y-4"
						style={{
							gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
							gridTemplateRows: `repeat(${ROWS}, minmax(0, 1fr))`,
						}}
					>
						{STATES.map((s, i) => (
							<motion.div
								key={s.code + i}
								initial={{ opacity: 0, scale: 0.6 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, margin: '-80px' }}
								transition={{
									duration: 0.4,
									delay: (i % 12) * 0.02,
								}}
								style={{
									gridColumn: s.c,
									gridRow: s.r,
								}}
								className="group aspect-square flex items-center justify-center border border-brand-navy/25 bg-brand-navy hover:bg-brand-red transition-colors relative"
							>
								<span className="font-chivo text-[9px] sm:text-[11px] font-black text-white tracking-widest">
									{s.code}
								</span>
							</motion.div>
						))}
					</div>

					<div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-brand-navy/15">
						<div className="flex items-center gap-6 text-[11px] font-chivo uppercase tracking-[0.25em] font-bold text-brand-navy/70">
							<span className="flex items-center gap-2">
								<span className="w-3 h-3 bg-brand-navy" />
								Covered
							</span>
							<span className="flex items-center gap-2">
								<span className="w-3 h-3 bg-brand-red" />
								Active Dispatch
							</span>
						</div>
						<a
							href="tel:+13072224082"
							className="btn-red !py-3 !px-5 text-sm sm:text-base"
						>
							<Phone className="w-4 h-4" strokeWidth={2.5} />
							Confirm your area — call
						</a>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default Coverage;
